import {
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  DockOutlined as DockIcon,
  ViewSidebarOutlined as DockLeftIcon,
  VerticalSplitOutlined as DockRightIcon,
  OpenInNew as OpenInNewIcon,
  PictureInPictureAlt as PictureInPictureAltIcon,
  QueryStats as QueryStatsIcon,
  Send as SendIcon,
  StopCircle as StopCircleIcon,
} from '@mui/icons-material'
import {
  Box,
  Chip,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  keyframes,
  Paper,
  Portal,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { useLeads } from 'api/aws/useLeads'
import { useEntitiesFromSearch } from 'api/search'
import { DockPosition, useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { GROBxLoader } from 'theme/components'
import { AgentMessage, parseRouteContext, sendAgentMessage, UsageStats } from '../../api/agent/useAgentChat'
import AgentClearCheck from './AgentClearCheck'
import AgentMarkdown from './AgentMarkdown'
import AgentModel from './AgentModel'
import ReferenceMenuBar from './ReferenceMenuBar'
import ReferencePanel from './ReferencePanel'
import UsagePanel, { accumulateUsage, SessionUsage } from './UsagePanel'
import { useAgentChatSync } from './useAgentChatSync'

interface AgentChatPanelProps {
  open?: boolean
  onClose?: () => void
  mode?: 'inline' | 'standalone' | 'docked'
}

const DEFAULT_WIDTH = 650
const DEFAULT_HEIGHT = 550
const MIN_WIDTH = 380
const MIN_HEIGHT = 300

const pulse = keyframes`
  0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
  40% { opacity: 1; transform: scale(1); }
`

const ProgressIndicator: React.FC<{ step: string }> = ({ step }) => (
  <Box sx={{ display: 'flex', gap: 0.75, py: 1.5, px: 1, alignItems: 'center' }}>
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        animation: `${pulse} 1s infinite ease-in-out`,
        flexShrink: 0,
      }}
    />
    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
      {step}
    </Typography>
  </Box>
)

const UserMessage: React.FC<{ content: string }> = ({ content }) => (
  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
    <Box
      sx={{
        maxWidth: '80%',
        px: 2,
        py: 1,
        borderRadius: 2.5,
        bgcolor: theme => (theme.palette.mode === 'light' ? 'primary.lighter' : 'background.default'),
        color: 'primary.contrastText',
      }}
    >
      <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', textAlign: 'right' }}>
        {content}
      </Typography>
    </Box>
  </Box>
)

const AssistantMessage: React.FC<{
  content: string
  onAction?: (text: string) => void
  onNavigate?: (pathname: string) => void
  deals?: any[]
  organizations?: any[]
  leads?: any[]
  isInteractive?: boolean
}> = ({ content, onAction, onNavigate, deals, organizations, leads, isInteractive }) => (
  <Box sx={{ mb: 1, px: 1 }}>
    <AgentMarkdown
      content={content}
      onAction={onAction}
      onNavigate={onNavigate}
      deals={deals}
      organizations={organizations}
      leads={leads}
      isInteractive={isInteractive}
    />
  </Box>
)

const AgentChatPanel: React.FC<AgentChatPanelProps> = ({ open = true, onClose, mode = 'inline' }) => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const location = useLocation()
  const isStandalone = mode === 'standalone'
  const isDocked = mode === 'docked'
  const layoutCtx = useLayoutContext()

  const { messages, setMessages, remotePathname, popOut, popIn, closePopout, navigateMain } = useAgentChatSync({
    isPopoutWindow: isStandalone,
  })

  const effectivePathname = isStandalone && remotePathname ? remotePathname : location.pathname
  const routeContext = useMemo(() => parseRouteContext(effectivePathname), [effectivePathname])
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')
  const { deals, organizations } = useEntitiesFromSearch()
  const { leads } = useLeads(true)

  const contextLabel = useMemo(() => {
    if (routeContext.page === 'deal' && routeContext.PK && routeContext.SK) {
      const deal = deals?.find(d => d?.PK === routeContext.PK && d?.SK === routeContext.SK)
      return deal?.name || null
    }
    if (routeContext.page === 'organization' && routeContext.PK) {
      const org = organizations?.find(o => o?.PK === routeContext.PK)
      return org?.name || null
    }
    return null
  }, [routeContext, deals, organizations])

  const [input, setInput] = useState('')
  const [isClearing, setIsClearing] = useState<boolean>(false)
  const [clearAnchor, setClearAnchor] = useState<HTMLElement | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [progressStep, setProgressStep] = useState<string | null>(null)
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const [sessionUsage, setSessionUsage] = useState<SessionUsage | null>(null)
  const [showUsage, setShowUsage] = useState(false)
  const [selectedModel, setSelectedModel] = useState<string>('sonnet-4.6')
  const [lastPayloadSize, setLastPayloadSize] = useState<number | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const cancelRef = useRef<(() => void) | null>(null)

  // Position & size state (inline only)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const [size, setSize] = useState({ w: DEFAULT_WIDTH, h: DEFAULT_HEIGHT })
  const [initialized, setInitialized] = useState(false)
  const dockedRef = useRef<HTMLDivElement>(null)

  // Drag state
  const dragRef = useRef<{ startX: number; startY: number; originX: number; originY: number } | null>(null)
  // Resize state
  const resizeRef = useRef<{ startX: number; startY: number; originW: number; originH: number } | null>(null)

  // Center on first open (inline only)
  useEffect(() => {
    if (!isStandalone && !isDocked && open && !initialized) {
      setPos({
        x: Math.round((window.innerWidth - DEFAULT_WIDTH) / 2),
        y: Math.round((window.innerHeight - DEFAULT_HEIGHT) / 2),
      })
      setSize({ w: DEFAULT_WIDTH, h: DEFAULT_HEIGHT })
      setInitialized(true)
    }
  }, [open, initialized, isStandalone, isDocked])

  // Auto-scroll to bottom on new messages, progress, or when panel opens
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: open ? 'instant' : 'smooth' })
  }, [messages, isLoading, progressStep, open])

  // Focus input when panel opens
  useEffect(() => {
    if (open || isStandalone || isDocked) {
      setTimeout(() => inputRef.current?.focus(), 200)
    }
  }, [open, isStandalone, isDocked])

  // ─── Drag handlers (inline only) ────────────────────────────────
  const onDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (isStandalone || isDocked) return
      if ((e.target as HTMLElement).closest('button')) return
      e.preventDefault()
      dragRef.current = { startX: e.clientX, startY: e.clientY, originX: pos.x, originY: pos.y }

      const onMove = (ev: MouseEvent) => {
        if (!dragRef.current) return
        const dx = ev.clientX - dragRef.current.startX
        const dy = ev.clientY - dragRef.current.startY
        setPos({
          x: Math.max(0, Math.min(window.innerWidth - 100, dragRef.current.originX + dx)),
          y: Math.max(0, Math.min(window.innerHeight - 50, dragRef.current.originY + dy)),
        })
      }
      const onUp = () => {
        dragRef.current = null
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    [pos, isStandalone]
  )

  // ─── Resize handlers (inline only) ──────────────────────────────
  const onResizeStart = useCallback(
    (e: React.MouseEvent) => {
      if (isStandalone) return
      e.preventDefault()
      e.stopPropagation()
      resizeRef.current = { startX: e.clientX, startY: e.clientY, originW: size.w, originH: size.h }

      const onMove = (ev: MouseEvent) => {
        if (!resizeRef.current) return
        const dx = ev.clientX - resizeRef.current.startX
        const dy = ev.clientY - resizeRef.current.startY
        setSize({
          w: Math.max(MIN_WIDTH, resizeRef.current.originW + dx),
          h: Math.max(MIN_HEIGHT, resizeRef.current.originH + dy),
        })
      }
      const onUp = () => {
        resizeRef.current = null
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }
      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    },
    [size, isStandalone]
  )

  // Core send function — used by both typed input and quick action buttons
  const sendMessageText = useCallback(
    (text: string) => {
      if (!text || isLoading) return

      const userMessage = { role: 'user' as const, content: text }
      const updatedMessages = [...messages, userMessage]
      setMessages(updatedMessages)
      setInput('')
      setIsLoading(true)
      setProgressStep('Thinking...')

      cancelRef.current = sendAgentMessage(
        updatedMessages as AgentMessage[],
        {
          onProgress: step => {
            setProgressStep(step)
          },
          onDone: response => {
            cancelRef.current = null
            setMessages(prev => [...prev, { role: 'assistant', content: response }])
            setIsLoading(false)
            setProgressStep(null)
          },
          onError: error => {
            cancelRef.current = null
            let displayError = error
            const jsonMatch = error.match(/^\d{3}\s+(\{[\s\S]+\})$/)
            if (jsonMatch) {
              try {
                const parsed = JSON.parse(jsonMatch[1])
                displayError = parsed?.error?.message ?? error
              } catch {
                /* keep original */
              }
            }
            setMessages(prev => [...prev, { role: 'assistant', content: `**Error:** ${displayError}` }])
            setIsLoading(false)
            setProgressStep(null)
          },
          onUsage: (usage: UsageStats) => {
            setSessionUsage(prev => accumulateUsage(prev, usage))
          },
          onPayloadSize: (bytes: number) => {
            setLastPayloadSize(bytes)
          },
        },
        routeContext,
        selectedModel
      )
    },
    [messages, isLoading, routeContext, setMessages, selectedModel]
  )

  const handleSend = useCallback(() => {
    const trimmed = input.trim()
    if (!trimmed) return
    sendMessageText(trimmed)
  }, [input, sendMessageText])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleStop = useCallback(() => {
    cancelRef.current?.()
    cancelRef.current = null
    setIsLoading(false)
    setProgressStep(null)
  }, [])
  const handleClick = event => {
    setClearAnchor(event.currentTarget)
    setIsClearing(true)
  }
  const handleCancel = () => {
    setIsClearing(false)
  }

  const handleClearMessages = useCallback(() => {
    setMessages([])
    setSessionUsage(null)
    setShowUsage(false)
    setIsClearing(false)
  }, [setMessages])

  const handleToggleMenu = useCallback((menuId: string) => {
    setActiveMenu(prev => (prev === menuId ? null : menuId))
  }, [])

  const handlePopOut = useCallback(() => {
    popOut()
    onClose?.()
  }, [popOut, onClose])

  // ─── Dock handlers ────────────────────────────────────────────────

  const handleDock = useCallback(
    (position: DockPosition) => {
      layoutCtx?.setAgentDock(position)
      // Close the floating panel if we're docking from inline
      if (mode === 'inline') onClose?.()
    },
    [layoutCtx, mode, onClose]
  )

  const handleUndock = useCallback(() => {
    layoutCtx?.setAgentDock(null)
    // Signal the nav bar to open the floating panel
    window.dispatchEvent(new CustomEvent('agent-undock'))
  }, [layoutCtx])

  // ─── Shared content ──────────────────────────────────────────────

  const isFloating = mode === 'inline'
  const canDrag = isFloating

  const headerContent = (
    <Box
      onMouseDown={canDrag ? onDragStart : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.section',
        flexShrink: 0,
        cursor: canDrag ? 'move' : 'default',
        userSelect: 'none',
        ...(canDrag && { '&:active': { cursor: 'move' } }),
      }}
    >
      {/* Top row: logo, model, title, controls */}
      <Box sx={{ height: 44, display: 'flex', alignItems: 'center', px: 1.5, gap: 1 }}>
        <Stack direction={'row'} sx={{ alignItems: 'center', justifyContent: 'center', gap: 1, overflow: 'hidden' }}>
          {/* <AnthropicLogo size={22} isOpen={false} isPoppedOut={false} /> */}
          <AgentModel selectedModel={selectedModel} setSelectedModel={setSelectedModel} />
        </Stack>
        <Typography
          variant="subtitle2"
          sx={{
            flex: 1,
            textAlign: 'center',
            fontWeight: 600,
            color: 'text.primary',
            fontSize: '0.8rem',
            lineHeight: 1.2,
          }}
        >
          GROBx AI
        </Typography>
        {messages.length > 0 && !isLoading && (
          <Tooltip title="Clear conversation">
            <IconButton size="small" onClick={handleClick} sx={{ color: 'text.secondary' }}>
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* Mode-specific controls */}
        {isStandalone ? (
          <Tooltip title="Return to main window">
            <IconButton size="small" onClick={popIn} sx={{ color: 'text.secondary' }}>
              <PictureInPictureAltIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : isDocked ? (
          <>
            {/* Dock position switcher */}
            <Tooltip title="Dock left">
              <IconButton
                size="small"
                onClick={() => handleDock('left')}
                sx={{ color: layoutCtx?.agentDock === 'left' ? 'primary.main' : 'text.secondary' }}
              >
                <DockLeftIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Dock right">
              <IconButton
                size="small"
                onClick={() => handleDock('right')}
                sx={{ color: layoutCtx?.agentDock === 'right' ? 'primary.main' : 'text.secondary' }}
              >
                <DockRightIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            {/* <Tooltip title="Dock bottom">
            <IconButton
              size="small"
              onClick={() => handleDock('bottom')}
              sx={{ color: layoutCtx?.agentDock === 'bottom' ? 'primary.main' : 'text.secondary' }}
            >
              <DockBottomIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip> */}
            <Tooltip title="Undock (floating)">
              <IconButton size="small" onClick={handleUndock} sx={{ color: 'text.secondary' }}>
                <DockIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </>
        ) : (
          <>
            {/* Floating mode: dock + popout + close */}
            <Tooltip title="Dock left">
              <IconButton
                size="small"
                onClick={() => handleDock('left')}
                sx={{ color: layoutCtx?.agentDock === 'left' ? 'primary.main' : 'text.secondary' }}
              >
                <DockLeftIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Dock right">
              <IconButton size="small" onClick={() => handleDock('right')} sx={{ color: 'text.secondary' }}>
                <DockRightIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open in new window">
              <IconButton size="small" onClick={handlePopOut} sx={{ color: 'text.secondary' }}>
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => {
                closePopout()
                onClose?.()
              }}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </>
        )}
      </Box>
      {/* Context row: route context label (e.g. deal name) */}
      {contextLabel && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 1.5,
            py: 0.5,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            minHeight: 28,
          }}
        >
          <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.2 }}>
            {contextLabel}
          </Typography>
        </Box>
      )}
    </Box>
  )

  const messagesContent = (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        px: 3,
        py: 2,
        bgcolor: theme.palette.background.gradient,
        '&::-webkit-scrollbar': { width: 5 },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: theme.palette.background.scrollbar_thumb,
          borderRadius: 3,
        },
      }}
    >
      {messages.length === 0 && !isLoading && (
        <Box sx={{ textAlign: 'center', mt: 10 }}>
          {/* <SmartToyIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1.5, opacity: 0.4 }} /> */}
          <GROBxLoader size={32} open={false} />
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            {currentUser ? `How can I help you today ${currentUser?.firstName}?` : 'How can I help you today? '}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
            Search organizations, look up contracts, create deals, and more.
          </Typography>
        </Box>
      )}
      {messages.map((msg, i) => {
        const isLastAssistant = msg.role === 'assistant' && !isLoading && i === messages.length - 1
        return (
          <React.Fragment key={i}>
            {msg.role === 'user' ? (
              <UserMessage content={msg.content} />
            ) : (
              <AssistantMessage
                content={msg.content}
                onAction={sendMessageText}
                onNavigate={isStandalone ? navigateMain : undefined}
                deals={deals}
                organizations={organizations}
                leads={leads ?? []}
                isInteractive={isLastAssistant}
              />
            )}
            {msg.role === 'assistant' && i < messages.length - 1 && <Divider sx={{ my: 2, opacity: 0.4 }} />}
          </React.Fragment>
        )
      })}
      {isLoading && progressStep && <ProgressIndicator step={progressStep} />}
      {lastPayloadSize !== null && import.meta.env.MODE !== 'production' && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'right',
            px: 1.5,
            py: 0.25,
            fontSize: '0.6rem',
            fontFamily: 'monospace',
            color:
              lastPayloadSize > 500_000 ? 'error.main' : lastPayloadSize > 100_000 ? 'warning.main' : 'text.disabled',
          }}
        >
          payload:{' '}
          {lastPayloadSize > 1_000_000
            ? `${(lastPayloadSize / 1_000_000).toFixed(2)} MB`
            : `${(lastPayloadSize / 1_000).toFixed(1)} KB`}{' '}
          ({messages.length} msgs)
        </Typography>
      )}
      <div ref={messagesEndRef} />
    </Box>
  )

  const inputContent = (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.section',
        flexShrink: 0,
      }}
    >
      <TextField
        inputRef={inputRef}
        fullWidth
        size="small"
        placeholder="Message GROBx AI..."
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={isLoading}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {isLoading ? (
                <IconButton onClick={handleStop} color="error" size="small">
                  <StopCircleIcon sx={{ fontSize: 18 }} />
                </IconButton>
              ) : (
                <IconButton onClick={handleSend} disabled={!input.trim()} color="primary" size="small">
                  <SendIcon sx={{ fontSize: 18 }} />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: 'background.default',
            fontSize: '0.875rem',
          },
        }}
      />
      <ReferenceMenuBar
        activeMenu={activeMenu}
        onToggleMenu={handleToggleMenu}
        extraChips={
          sessionUsage && (
            <Chip
              icon={<QueryStatsIcon sx={{ fontSize: 14 }} />}
              label={`$${sessionUsage.actualCost.toFixed(4)}`}
              size="small"
              variant={showUsage ? 'filled' : 'outlined'}
              onClick={() => setShowUsage(v => !v)}
              sx={{
                cursor: 'pointer',
                fontSize: '0.7rem',
                height: 24,
                p: 1.0,
                borderColor: 'text.ai_agent_text',
                color: showUsage ? 'text.primary' : 'text.ai_agent_text',
                bgcolor: showUsage ? 'action.selected' : 'transparent',
                fontFamily: 'monospace',
                transition: 'all 0.2s ease',
                '& .MuiChip-icon': {
                  color: showUsage ? 'text.primary' : 'text.ai_agent_text',
                },
                '&:hover': {
                  borderColor: 'text.highlight',
                  color: 'text.primary',
                  '& .MuiChip-icon': { color: 'text.primary' },
                },
              }}
            />
          )
        }
      />
    </Box>
  )

  // ─── Docked mode ───────────────────────────────────────────────

  // Compute docked panel bounds for reference/usage panels
  const dockedBounds = useMemo(() => {
    if (!isDocked || !dockedRef.current) return { pos: { x: 0, y: 0 }, size: { w: 400, h: window.innerHeight } }
    const rect = dockedRef.current.getBoundingClientRect()
    return { pos: { x: rect.left, y: rect.top }, size: { w: rect.width, h: rect.height } }
  }, [isDocked, activeMenu, showUsage])

  if (isDocked) {
    return (
      <Box
        ref={dockedRef}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.ai_agent,
          pt: pinned ? '60px' : '0px',
          transition: 'padding 0.3s ease',
          overflow: 'hidden',
        }}
      >
        {headerContent}
        {messagesContent}
        {inputContent}
        {activeMenu && (
          <ReferencePanel
            menuId={activeMenu}
            pos={dockedBounds.pos}
            size={dockedBounds.size}
            onClose={() => setActiveMenu(null)}
            onSelectPrompt={(prompt) => { setInput(prompt); setActiveMenu(null) }}
            isDocked
          />
        )}
        {showUsage && sessionUsage && (
          <UsagePanel
            usage={sessionUsage}
            pos={dockedBounds.pos}
            size={dockedBounds.size}
            onClose={() => setShowUsage(false)}
          />
        )}
        {isClearing && (
          <AgentClearCheck
            isClearing={isClearing}
            clearAnchor={clearAnchor}
            onClear={handleClearMessages}
            onCancel={handleCancel}
          />
        )}
      </Box>
    )
  }

  // ─── Standalone mode ─────────────────────────────────────────────
  if (isStandalone) {
    const standalonePos = { x: 0, y: 0 }
    const standaloneSize = { w: window.innerWidth, h: window.innerHeight }

    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.ai_agent,
        }}
      >
        {headerContent}
        {messagesContent}
        {inputContent}
        {activeMenu && (
          <ReferencePanel
            menuId={activeMenu}
            pos={standalonePos}
            size={standaloneSize}
            onClose={() => setActiveMenu(null)}
          />
        )}
        {showUsage && sessionUsage && (
          <UsagePanel
            usage={sessionUsage}
            pos={standalonePos}
            size={standaloneSize}
            onClose={() => setShowUsage(false)}
          />
        )}
        {isClearing && (
          <AgentClearCheck
            isClearing={isClearing}
            clearAnchor={clearAnchor}
            onClear={handleClearMessages}
            onCancel={handleCancel}
          />
        )}
      </Box>
    )
  }

  // ─── Inline mode ─────────────────────────────────────────────────
  if (!open && !initialized) return null

  return (
    <Portal>
      <Fade in={open} timeout={200} unmountOnExit>
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            top: pos.y,
            left: pos.x,
            width: size.w,
            height: size.h,
            zIndex: 1300,
            borderRadius: 2,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: theme.palette.background.ai_agent,
            boxShadow: `8px 2px 22px ${theme.palette.background.section}`,
          }}
        >
          {headerContent}
          {messagesContent}
          {inputContent}

          {/* Resize handle */}
          <Box
            onMouseDown={onResizeStart}
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 16,
              height: 16,
              cursor: 'nwse-resize',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 3,
                right: 3,
                width: 8,
                height: 8,
                borderRight: '2px solid',
                borderBottom: '2px solid',
                borderColor: 'text.disabled',
                opacity: 0.5,
              },
            }}
          />
        </Paper>
      </Fade>

      {open && activeMenu && (
        <ReferencePanel menuId={activeMenu} pos={pos} size={size} onClose={() => setActiveMenu(null)} onSelectPrompt={(prompt) => { setInput(prompt); setActiveMenu(null) }} />
      )}
      {open && showUsage && sessionUsage && (
        <UsagePanel usage={sessionUsage} pos={pos} size={size} onClose={() => setShowUsage(false)} />
      )}
      {isClearing && (
        <AgentClearCheck
          isClearing={isClearing}
          clearAnchor={clearAnchor}
          onClear={handleClearMessages}
          onCancel={handleCancel}
        />
      )}
    </Portal>
  )
}

export default AgentChatPanel
